import { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    MenuItem,
    Tabs,
    Tab,
    useTheme,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSignOut } from 'react-auth-kit';
import Axios from 'axios';
import Header from '../../components/Header';
import Loading from '../../components/Loading';
import { tokens } from '../../theme';
import { getAuthHeader } from '../../utils/authToken';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import DraftIcon from '@mui/icons-material/Edit';
// import ArchiveIcon from '@mui/icons-material/Archive'; // Commented out - no server endpoint
// import UnarchiveIcon from '@mui/icons-material/Unarchive'; // Commented out - no server endpoint
import PublishIcon from '@mui/icons-material/Publish';
import HistoryIcon from '@mui/icons-material/History';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import { useSnackbar } from 'notistack';
import Alert from '../../components/alert/Alert';
import AllCompetitionsUsageModal from './AllCompetitionsUsageModal';
import GlobalCompetitionUsageModal from './GlobalCompetitionUsageModal';

const Newsletters = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();
    const location = useLocation();
    const signOut = useSignOut();
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [newsletters, setNewsletters] = useState([]);
    const [error, setError] = useState('');

    // Newsletter type tab — 'competition' (existing) or 'admission' (new).
    // Tab is derived from the URL path so deep links work both directions:
    //   /admin/newsletters/competition  → Competition tab
    //   /admin/newsletters/admission    → Admission tab
    //   /admin/newsletters              → Competition (default)
    const typeTab = location.pathname.endsWith('/admission') ? 'admission' : 'competition';

    // Filters
    const [monthFilter, setMonthFilter] = useState('all');
    const [yearFilter, setYearFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    // Delete dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [newsletterToDelete, setNewsletterToDelete] = useState(null);

    // Usage modal
    const [usageModalOpen, setUsageModalOpen] = useState(false);
    const [selectedNewsletterForUsage, setSelectedNewsletterForUsage] = useState(null);

    // Global usage modal
    const [globalUsageModalOpen, setGlobalUsageModalOpen] = useState(false);

    // Alert
    const [successAlertOpen, setSuccessAlertOpen] = useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = useState('');
    const [errorAlertOpen, setErrorAlertOpen] = useState(false);
    const [errorAlertMessage, setErrorAlertMessage] = useState('');

    // Polling for active newsletters
    const intervalRef = useRef(null);

    const StyledTableCell = styled(TableCell)(() => ({
        [`&.${tableCellClasses.head}`]: {
            backgroundColor: colors.primary[300],
            color: 'white',
            fontSize: 14,
            fontWeight: 'bold',
        },
        [`&.${tableCellClasses.body}`]: {
            fontSize: 14,
        },
        border: '1px solid rgba(224, 224, 224, 1)',
    }));

    const StyledTableRow = styled(TableRow)(() => ({
        '&:nth-of-type(odd)': {
            backgroundColor: colors.primary[750],
        },
        '&:nth-of-type(even)': {
            backgroundColor: colors.primary[750],
        },
        '&:hover': {
            backgroundColor: colors.primary[700],
        }
    }));

    const handleLogout = () => {
        signOut();
        localStorage.removeItem('userName');
        localStorage.removeItem('who');
        navigate('/signin');
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'processing': return 'info';
            case 'completed': return 'success';
            case 'failed': return 'error';
            case 'draft': return 'default';
            case 'published': return 'success';
            case 'archived': return 'default';
            default: return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending': return <HourglassEmptyIcon fontSize="small" />;
            case 'processing': return <SyncIcon fontSize="small" />;
            case 'completed': return <CheckCircleIcon fontSize="small" />;
            case 'failed': return <ErrorIcon fontSize="small" />;
            case 'draft': return <DraftIcon fontSize="small" />;
            case 'published': return <PublishIcon fontSize="small" />;
            // case 'archived': return <ArchiveIcon fontSize="small" />;
            default: return null;
        }
    };

    const fetchNewsletters = async () => {
        try {
            const params = { type: typeTab };
            if (monthFilter !== 'all') params.month = monthFilter;
            if (yearFilter !== 'all') params.year = yearFilter;
            if (statusFilter !== 'all') params.status = statusFilter;

            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/newsletters`,
                {
                    headers: { Authorization: getAuthHeader() },
                    params
                }
            );

            // Ensure we always set an array
            if (Array.isArray(response.data)) {
                setNewsletters(response.data);
            } else if (response.data && Array.isArray(response.data.newsletters)) {
                setNewsletters(response.data.newsletters);
            } else {
                setNewsletters([]);
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching newsletters:', error);
            setNewsletters([]); // Always set to empty array on error

            if (error.response?.status === 401) {
                handleLogout();
            } else if (error.response?.status === 404) {
                // Already set to empty array above
                setLoading(false);
            } else {
                setError('Failed to load newsletters');
                setErrorAlertMessage('Failed to load newsletters');
                setErrorAlertOpen(true);
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        localStorage.setItem('selectedMenu', 'Newsletters');
        fetchNewsletters();
    }, [typeTab, monthFilter, yearFilter, statusFilter]);

    // Polling for processing/pending newsletters
    useEffect(() => {
        // Ensure newsletters is an array before filtering
        if (!Array.isArray(newsletters)) {
            return;
        }

        const activeNewsletters = newsletters.filter(n =>
            n.status === 'processing' || n.status === 'pending'
        );

        if (activeNewsletters.length > 0) {
            intervalRef.current = setInterval(() => {
                fetchNewsletters();
            }, 10000); // Poll every 10 seconds
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [newsletters]);

    const handleView = (newsletter) => {
        if (newsletter.status === 'processing' || newsletter.status === 'pending') {
            navigate(`/admin/newsletters/progress/${newsletter.uuid}`);
        } else {
            navigate(`/admin/newsletters/${newsletter.uuid}`);
        }
    };

    const handleDelete = async () => {
        if (!newsletterToDelete) return;

        try {
            await Axios.delete(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${newsletterToDelete.uuid}`,
                { headers: { Authorization: getAuthHeader() } }
            );

            setSuccessAlertMessage('Newsletter deleted successfully');
            setSuccessAlertOpen(true);
            setDeleteDialogOpen(false);
            setNewsletterToDelete(null);
            fetchNewsletters();
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                setErrorAlertMessage('Failed to delete newsletter');
                setErrorAlertOpen(true);
            }
        }
    };

    const handleRegenerate = async (newsletter) => {
        try {
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${newsletter.uuid}/retry`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );

            setSuccessAlertMessage('Newsletter regeneration started');
            setSuccessAlertOpen(true);
            navigate(`/admin/newsletters/progress/${response.data.uuid}`);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                setErrorAlertMessage(error.response?.data?.error || 'Failed to regenerate newsletter');
                setErrorAlertOpen(true);
            }
        }
    };

    // Archive function - commented out (no server endpoint)
    // const handleArchive = async (newsletter) => {
    //     try {
    //         await Axios.post(
    //             `${process.env.REACT_APP_URL}/api/v1/newsletters/${newsletter.uuid}/archive`,
    //             {},
    //             { headers: { Authorization: getAuthHeader() } }
    //         );

    //         setSuccessAlertMessage('Newsletter archived successfully');
    //         setSuccessAlertOpen(true);
    //         fetchNewsletters();
    //     } catch (error) {
    //         if (error.response?.status === 401) {
    //             handleLogout();
    //         } else {
    //             setErrorAlertMessage('Failed to archive newsletter');
    //             setErrorAlertOpen(true);
    //         }
    //     }
    // };

    const handleViewUsage = (newsletter) => {
        setSelectedNewsletterForUsage(newsletter);
        setUsageModalOpen(true);
    };

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

    return (
        <Box m="20px" marginX={3} marginY={3}>
            <Header title="Newsletters" subtitle="AI-Powered Monthly Newsletters" />

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs
                    value={typeTab}
                    onChange={(e, v) => {
                        // Reset filters when switching tabs so a status that
                        // doesn't exist in the other type doesn't get carried.
                        setMonthFilter('all');
                        setYearFilter('all');
                        setStatusFilter('all');
                        navigate(`/admin/newsletters/${v}`);
                    }}
                    aria-label="Newsletter type tabs"
                >
                    <Tab value="competition" label="Competition" />
                    <Tab value="admission" label="Admission" />
                </Tabs>
            </Box>

            {loading ? (
                <Loading />
            ) : (
                <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box display="flex" gap={2}>
                            <TextField
                                select
                                label="Month"
                                value={monthFilter}
                                onChange={(e) => setMonthFilter(e.target.value)}
                                size="small"
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value="all">All Months</MenuItem>
                                {monthNames.map((month, index) => (
                                    <MenuItem key={index + 1} value={index + 1}>
                                        {month}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                label="Year"
                                value={yearFilter}
                                onChange={(e) => setYearFilter(e.target.value)}
                                size="small"
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value="all">All Years</MenuItem>
                                {years.map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                label="Status"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                size="small"
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value="all">All Status</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="processing">Processing</MenuItem>
                                <MenuItem value="completed">Completed</MenuItem>
                                <MenuItem value="failed">Failed</MenuItem>
                                <MenuItem value="draft">Draft</MenuItem>
                                <MenuItem value="published">Published</MenuItem>
                                {/* <MenuItem value="archived">Archived</MenuItem> */} {/* Commented out - no server endpoint */}
                            </TextField>
                        </Box>

                        <Box display="flex" gap={1}>
                            {typeTab === 'competition' && (
                                <Tooltip title="View All Competition Usage History">
                                    <IconButton
                                        onClick={() => setGlobalUsageModalOpen(true)}
                                        sx={{
                                            backgroundColor: colors.primary[300],
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: colors.primary[400]
                                            }
                                        }}
                                    >
                                        <HistoryIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => navigate(`/admin/newsletters/generate?type=${typeTab}`)}
                                sx={{
                                    backgroundColor: colors.primary[300],
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: colors.primary[400]
                                    }
                                }}
                            >
                                {typeTab === 'admission' ? 'Generate Admission Newsletter' : 'Generate Newsletter'}
                            </Button>
                        </Box>
                    </Box>

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Month/Year</StyledTableCell>
                                    <StyledTableCell>Title</StyledTableCell>
                                    <StyledTableCell>Status</StyledTableCell>
                                    <StyledTableCell>Created</StyledTableCell>
                                    <StyledTableCell>Updated</StyledTableCell>
                                    <StyledTableCell align="center">Actions</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {newsletters.length === 0 ? (
                                    <StyledTableRow>
                                        <StyledTableCell colSpan={6} align="center">
                                            <Typography variant="h6" color={colors.grey[500]}>
                                                No newsletters found
                                            </Typography>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ) : (
                                    newsletters.map((newsletter) => (
                                        <StyledTableRow key={newsletter.uuid}>
                                            <StyledTableCell>
                                                {monthNames[newsletter.month - 1]} {newsletter.year}
                                            </StyledTableCell>
                                            <StyledTableCell>{newsletter.title}</StyledTableCell>
                                            <StyledTableCell>
                                                <Chip
                                                    icon={getStatusIcon(newsletter.status)}
                                                    label={newsletter.status.toUpperCase()}
                                                    color={getStatusColor(newsletter.status)}
                                                    size="small"
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {new Date(newsletter.createdAt).toLocaleDateString()}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {new Date(newsletter.updatedAt).toLocaleDateString()}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                <Box display="flex" gap={1} justifyContent="center">
                                                    <Tooltip title="View">
                                                        <IconButton
                                                            onClick={() => handleView(newsletter)}
                                                            size="small"
                                                            color="primary"
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    {typeTab === 'competition' && ['completed', 'draft', 'published'].includes(newsletter.status) && (
                                                        <Tooltip title="View Competition Usage">
                                                            <IconButton
                                                                onClick={() => handleViewUsage(newsletter)}
                                                                size="small"
                                                                color="info"
                                                            >
                                                                <HistoryIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {(newsletter.status === 'completed' || newsletter.status === 'failed') && (
                                                        <Tooltip title={newsletter.status === 'failed' ? 'Retry' : 'Regenerate'}>
                                                            <IconButton
                                                                onClick={() => handleRegenerate(newsletter)}
                                                                size="small"
                                                                color="info"
                                                            >
                                                                <RefreshIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {/* Archive functionality - commented out (no server endpoint) */}
                                                    {/* {newsletter.status !== 'archived' && newsletter.status !== 'processing' && newsletter.status !== 'pending' && (
                                                        <Tooltip title="Archive">
                                                            <IconButton
                                                                onClick={() => handleArchive(newsletter)}
                                                                size="small"
                                                                color="warning"
                                                            >
                                                                <ArchiveIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )} */}

                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            onClick={() => {
                                                                setNewsletterToDelete(newsletter);
                                                                setDeleteDialogOpen(true);
                                                            }}
                                                            size="small"
                                                            color="error"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Delete Newsletter</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the newsletter "{newsletterToDelete?.title}"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Competition Usage Modal */}
            <AllCompetitionsUsageModal
                open={usageModalOpen}
                onClose={() => setUsageModalOpen(false)}
                newsletter={selectedNewsletterForUsage}
            />

            {/* Global Competition Usage Modal */}
            <GlobalCompetitionUsageModal
                open={globalUsageModalOpen}
                onClose={() => setGlobalUsageModalOpen(false)}
            />

            <Alert
                successAlertOpen={successAlertOpen}
                setSuccessAlertOpen={setSuccessAlertOpen}
                errorAlertOpen={errorAlertOpen}
                setErrorAlertOpen={setErrorAlertOpen}
                successMsg={successAlertMessage}
                errorMsg={errorAlertMessage}
                vertical='bottom'
                horizontal='left'
            />
        </Box>
    );
};

export default Newsletters;
